import { ownerRoleIsUnassigned } from './incidentPatternVocabulary';

type ReviewerCaseStateTone = 'warning' | 'info' | 'success' | 'default';

export type ReviewerCaseStateKey =
  | 'awaiting_review'
  | 'preparing_evidence'
  | 'reviewer_engaged'
  | 'closed';

export type ReviewerCaseState = {
  key: ReviewerCaseStateKey;
  label: string;
  helper: string;
  consequence: string;
  actionNeeded: boolean;
  tone: ReviewerCaseStateTone;
};

type ReviewerCaseStateInput = {
  status?: string | null;
  ops_state?: string | null;
  canonical_pattern_owner?: string | null;
  review_readiness?: string | null;
  started_at?: string | null;
  first_seen?: string | null;
  created_at?: string | null;
};

function openedDays(input: ReviewerCaseStateInput): number | null {
  const raw = String(input.started_at || input.first_seen || input.created_at || '').trim();
  if (!raw) return null;
  const ts = Date.parse(raw);
  if (!Number.isFinite(ts)) return null;
  const days = Math.floor((Date.now() - ts) / 86_400_000);
  return Math.max(0, days);
}

export function reviewerCaseOpenedDays(input: ReviewerCaseStateInput): number | null {
  return openedDays(input);
}

function waitingConsequence(daysOpen: number | null): string {
  if (daysOpen == null || daysOpen <= 0) {
    return 'A reviewer still needs to inspect representative evidence and decide whether this pattern requires management.';
  }
  return `Open ${daysOpen} day${daysOpen === 1 ? '' : 's'} without review. A reviewer still needs to inspect representative evidence and decide whether this pattern requires management.`;
}

export function deriveReviewerCaseState(input: ReviewerCaseStateInput): ReviewerCaseState {
  const status = String(input.status || '').trim().toUpperCase();
  const opsState = String(input.ops_state || '').trim().toLowerCase();
  const reviewReadiness = String(input.review_readiness || '').trim().toLowerCase();
  const hasPatternOwner = !ownerRoleIsUnassigned(input.canonical_pattern_owner);
  const daysOpen = openedDays(input);

  if (status === 'CLOSED' || status === 'SUPPRESSED' || opsState === 'closed' || opsState === 'resolved') {
    return {
      key: 'closed',
      label: 'Closed — no action needed',
      helper: 'This pattern is resolved or suppressed. Monitoring history stays available if you need to drill in.',
      consequence: 'No immediate reviewer action is required for this pattern.',
      actionNeeded: false,
      tone: 'success',
    };
  }

  if (reviewReadiness === 'evidence_preparing' || reviewReadiness === 'no_eligible_anchor') {
    return {
      key: 'preparing_evidence',
      label: 'Preparing evidence',
      helper: 'Representative evidence is still being prepared before the pattern can be reviewed cleanly.',
      consequence: 'Monitoring is active, but no reviewer action is required until representative evidence is ready.',
      actionNeeded: false,
      tone: 'default',
    };
  }

  if (
    status === 'ACKED'
    || status === 'IN_REVIEW'
    || opsState === 'acknowledged'
    || opsState === 'in_progress'
    || hasPatternOwner
  ) {
    return {
      key: 'reviewer_engaged',
      label: 'Awaiting your review',
      helper: 'Assigned to the current reviewer. Review representative evidence; you do not need to work every observation row.',
      consequence: 'Review representative evidence and decide whether this pattern requires management. Engagement does not change workflow progression.',
      actionNeeded: true,
      tone: 'info',
    };
  }

  return {
    key: 'awaiting_review',
    label: 'Needs review',
    helper: '1 pattern needs review. Review representative evidence; you do not need to work every observation row.',
    consequence: waitingConsequence(daysOpen),
    actionNeeded: true,
    tone: 'warning',
  };
}
